import { useMemo, useState } from 'react';
import type { Employee } from '@/lib/api/employees';
import { LargeTitleHeader, Pagination, SegmentedControl } from '@/components/common';

interface EmployeesTableProps {
  employees: Employee[];
  loading?: boolean;
  onOpen: (id: string) => void;
  onEdit: (employee: Employee) => void;
  onToggleStatus: (employee: Employee) => void;
  onAdd: () => void;
  onExport?: () => void;
  /** Send (or re-send) a portal invite / access-reset link for this employee. */
  onInvite?: (employee: Employee) => void;
}

type SortKey = 'name' | 'role' | 'crew' | 'status';
type StatusOption = 'All' | 'Active' | 'Inactive';

// Jobs-style main-area list for /employees: LargeTitleHeader + search + status
// SegmentedControl + type quick-filter chips + sortable table + pagination.
export const EmployeesTable = ({ employees, loading, onOpen, onEdit, onToggleStatus, onAdd, onExport, onInvite }: EmployeesTableProps) => {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusOption>('All');
  const [typeFilter, setTypeFilter] = useState<'all' | 'employee' | 'subcontractor'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [menuId, setMenuId] = useState<string | null>(null);
  // Fixed-position coords so the menu escapes the table's overflow-x-auto clipping
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const openMenu = (e: React.MouseEvent, id: string) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, right: Math.max(8, window.innerWidth - rect.right) });
    setMenuId(menuId === id ? null : id);
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = employees;
    if (status !== 'All') list = list.filter(e => e.status === status.toLowerCase());
    if (typeFilter !== 'all') list = list.filter(e => (e.employee_type || 'employee') === typeFilter);
    if (q) {
      list = list.filter(e =>
        [e.name, e.role, e.crew, e.email, e.phone].some(v => (v || '').toLowerCase().includes(q)));
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) =>
      dir * String(a[sortKey] || '').localeCompare(String(b[sortKey] || '')));
  }, [employees, query, status, typeFilter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const rows = filtered.slice((safePage - 1) * perPage, safePage * perPage);

  const sortIcon = (key: SortKey) =>
    sortKey !== key
      ? <span className="material-symbols-outlined text-base opacity-30">unfold_more</span>
      : <span className="material-symbols-outlined text-base text-primary">{sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>;

  const statusClass = (s: string) =>
    s === 'active' ? 'bg-success/10 text-success ring-1 ring-inset ring-success/20'
      : 'bg-gray-500/10 text-gray-500 ring-1 ring-inset ring-gray-500/20';

  const typeChips: { key: 'employee' | 'subcontractor'; label: string; icon: string }[] = [
    { key: 'employee', label: 'Staff', icon: 'badge' },
    { key: 'subcontractor', label: 'Subs', icon: 'engineering' },
  ];

  return (
    <div className="flex flex-col gap-4">
      <LargeTitleHeader
        title="Employees"
        subtitle="Team Directory"
        actions={
          <div className="flex items-center gap-2">
            {onExport && (
              <button
                onClick={onExport}
                className="flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 transition-colors"
                title="Export CSV"
              >
                <span className="material-symbols-outlined text-lg">download</span>
              </button>
            )}
            <button
              onClick={onAdd}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90"
            >
              <span className="material-symbols-outlined text-base">add</span>
              New Employee
            </button>
          </div>
        }
      >
        <div className="flex flex-wrap items-center gap-3 pb-1">
          <div className="flex-1 min-w-[180px] max-w-sm">
            <label className="relative flex flex-col">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-base">search</span>
              <input
                type="text"
                className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border-gray-200 bg-gray-50 h-9 placeholder:text-gray-500 pl-9 pr-3 text-sm font-normal leading-normal"
                placeholder="Search employees…"
                value={query}
                onChange={e => { setQuery(e.target.value); setPage(1); }}
                onKeyDown={e => { if (e.key === 'Escape') { setQuery(''); (e.target as HTMLInputElement).blur(); } }}
              />
            </label>
          </div>
          <SegmentedControl<StatusOption>
            options={[{ value: 'All', label: 'All' }, { value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }]}
            value={status}
            onChange={v => { setStatus(v); setPage(1); }}
            size="sm"
            fullWidth={false}
          />
        </div>
      </LargeTitleHeader>

      {/* Quick-filter chips (jobs-style) */}
      <div className="flex flex-wrap items-center gap-2">
        {typeChips.map(chip => (
          <button
            key={chip.key}
            onClick={() => { setTypeFilter(t => (t === chip.key ? 'all' : chip.key)); setPage(1); }}
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${
              typeFilter === chip.key
                ? 'bg-primary text-white border-primary'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <span className="material-symbols-outlined text-sm">{chip.icon}</span>
            {chip.label}
          </button>
        ))}
        <span className="text-xs text-gray-500 ml-1">{filtered.length} member{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <div className="rounded-xl bg-white border border-gray-200 min-h-[100px]">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-primary/5" onClick={() => toggleSort('name')}>
                  <div className="flex items-center gap-2">Name {sortIcon('name')}</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-primary/5 hidden sm:table-cell" onClick={() => toggleSort('role')}>
                  <div className="flex items-center gap-2">Role {sortIcon('role')}</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-primary/5 hidden md:table-cell" onClick={() => toggleSort('crew')}>
                  <div className="flex items-center gap-2">Crew {sortIcon('crew')}</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-primary/5" onClick={() => toggleSort('status')}>
                  <div className="flex items-center gap-2">Status {sortIcon('status')}</div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                [1, 2, 3, 4, 5].map(i => (
                  <tr key={i}>
                    <td className="px-4 py-3"><div className="h-3.5 bg-gray-100 rounded animate-pulse w-32" /></td>
                    <td className="px-4 py-3 hidden sm:table-cell"><div className="h-3.5 bg-gray-100 rounded animate-pulse w-24" /></td>
                    <td className="px-4 py-3 hidden md:table-cell"><div className="h-3.5 bg-gray-100 rounded animate-pulse w-20" /></td>
                    <td className="px-4 py-3"><div className="h-5 bg-gray-100 rounded-full animate-pulse w-16" /></td>
                    <td className="px-4 py-3"><div className="h-6 bg-gray-100 rounded animate-pulse w-8 ml-auto" /></td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-16 text-center">
                    <span className="material-symbols-outlined text-gray-300 text-5xl block mx-auto mb-3">badge</span>
                    <p className="text-gray-500 text-sm font-medium">No employees found</p>
                    <p className="text-gray-400 text-xs mt-1">Try adjusting your filters or search</p>
                  </td>
                </tr>
              ) : (
                rows.map(emp => (
                  <tr key={emp.id} className="hover:bg-primary/5 transition-colors cursor-pointer" onClick={() => onOpen(emp.id)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="bg-primary/10 text-primary rounded-full h-9 w-9 flex items-center justify-center flex-shrink-0 font-bold overflow-hidden">
                          {emp.profile_image
                            ? <img src={emp.profile_image} alt="" className="h-full w-full object-cover" />
                            : emp.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <span className="block text-sm font-semibold text-gray-900 truncate">{emp.name}</span>
                          {emp.employee_type === 'subcontractor' && <span className="text-[10px] text-gray-400 uppercase tracking-wide">Sub</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">{emp.role || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{emp.crew || 'Unassigned'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium capitalize ${statusClass(emp.status)}`}>
                        {emp.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                      <div className="relative inline-block">
                        <button
                          onClick={e => openMenu(e, emp.id)}
                          className="text-gray-600 hover:text-primary p-2 rounded-lg hover:bg-primary/10"
                        >
                          <span className="material-symbols-outlined text-base">more_vert</span>
                        </button>
                        {menuId === emp.id && (
                          <>
                            <div className="fixed inset-0 z-[9998]" onClick={() => setMenuId(null)} />
                            <div
                              className="fixed bg-white border border-gray-200 shadow-2xl rounded-lg w-48 z-[9999] py-1"
                              style={{ top: menuPos.top, right: menuPos.right }}
                            >
                              <button className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-900 hover:bg-primary/10 text-left" onClick={() => { onOpen(emp.id); setMenuId(null); }}>
                                <span className="material-symbols-outlined text-base">open_in_new</span> Open
                              </button>
                              <button className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-900 hover:bg-primary/10 text-left" onClick={() => { onEdit(emp); setMenuId(null); }}>
                                <span className="material-symbols-outlined text-base">edit</span> Edit
                              </button>
                              {onInvite && (
                                <button className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-900 hover:bg-primary/10 text-left" onClick={() => { onInvite(emp); setMenuId(null); }}>
                                  <span className="material-symbols-outlined text-base">key</span>
                                  {emp.user_id ? 'Reset portal access' : 'Send portal invite'}
                                </button>
                              )}
                              <button className={`flex w-full items-center gap-3 px-4 py-2 text-sm text-left ${emp.status === 'active' ? 'text-error hover:bg-error/10' : 'text-success hover:bg-success/10'}`} onClick={() => { onToggleStatus(emp); setMenuId(null); }}>
                                <span className="material-symbols-outlined text-base">{emp.status === 'active' ? 'no_accounts' : 'check_circle'}</span>
                                {emp.status === 'active' ? 'Deactivate' : 'Activate'}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && filtered.length > 0 && (
        <Pagination
          currentPage={safePage}
          totalPages={totalPages}
          totalItems={filtered.length}
          itemsPerPage={perPage}
          onPageChange={setPage}
          onItemsPerPageChange={n => { setPerPage(n); setPage(1); }}
        />
      )}
    </div>
  );
};
